<template>
    <div>
        <advanced-filter-btn :label="displayName" :active="hasAppliedFilter" data-test="students3d-filter-by-attendance-btn" @click="dialog=true" />

        <v-dialog v-model="dialog" width="400" :persistent="persistDialog">
            <fe-stepper-filter
                :title="title"
                style="height:672px;"
                :applyDisabled="!formValid || !enableFinalPanel"
                @apply="emit"
                @close="dialog=false"
            >
                <fe-stepper-panel width="100%" style="padding:20px;">
                    <v-form lazy-validation v-model="formValid">
                        <fe-remote-combo
                            v-model="panelValues.occurrence_month_num"
                            label="Month(s)"
                            itemText="name"
                            itemValue="value"
                            :items="months"
                            :disabled="panelValues.occurrence_weekday_num && panelValues.occurrence_weekday_num.length > 0"
                            multiple
                            moreText
                        />

                        <fe-remote-combo
                            v-model="panelValues.occurrence_weekday_num"
                            label="Weekday(s)"
                            itemText="name"
                            itemValue="value"
                            :items="days"
                            :disabled="panelValues.occurrence_month_num && panelValues.occurrence_month_num.length > 0"
                            multiple
                            moreText
                        />

                        <fe-remote-combo
                            v-model="panelValues.filter_by"
                            label="Filter by"
                            :items="filter_by_options"
                            byId
                        />

                        <v-layout v-if="panelValues.filter_by === 'rate'" row>
                            <v-flex xs6 class="mr-2">
                                <label for="attendance-min-rate" class="theme--light v-label">Min Rate</label>
                                <v-text-field
                                    id="attendance-min-rate"
                                    v-model="panelValues.attendance_value_min"
                                    v-mask="'###'"
                                    append-icon="far fa-percent"
                                    :error-messages="minError"
                                    flat solo dense
                                />
                            </v-flex>

                            <v-flex xs6 class="">
                                <label for="attendance-max-rate" class="theme--light v-label">Max Rate</label>
                                <v-text-field
                                    id="attendance-max-rate"
                                    v-model="panelValues.attendance_value_max"
                                    v-mask="'###'"
                                    append-icon="far fa-percent"
                                    :error-messages="maxError"
                                    flat solo dense
                                />
                            </v-flex>
                        </v-layout>

                        <v-layout v-else row>
                            <v-flex xs6 class="mr-2">
                                <label for="attendance-min-days-missed" class="theme--light v-label">Min Days Missed</label>
                                <v-text-field
                                    id="attendance-min-days-missed"
                                    v-model="panelValues.attendance_value_min"
                                    v-mask="'###'"
                                    :error-messages="minError"
                                    flat solo dense
                                />
                            </v-flex>

                            <v-flex xs6 class="">
                                <label for="attendance-max-days-missed" class="theme--light v-label">Max Days Missed</label>
                                <v-text-field
                                    id="attendance-max-days-missed"
                                    v-model="panelValues.attendance_value_max"
                                    v-mask="'###'"
                                    :error-messages="maxError"
                                    flat solo dense
                                />
                            </v-flex>
                        </v-layout>

                        <v-layout row align-start>
                            <v-flex xs6 class="mr-2 attendance-type-cell" :class="{ 'attendance-type-field--error': attendanceTypeNotConfigured }">
                                <label for="attendance-type" class="theme--light v-label d-flex align-center attendance-type-label row-label" :class="{ 'error--text': attendanceTypeNotConfigured }">
                                    Attendance Type
                                    <fe-tooltip v-if="attendanceTypeNotConfigured" tooltip="No attendance rate types are enabled for your district. Please contact an administrator for help." class="ml-1 d-inline-block">
                                        <i class="fal fa-exclamation-circle error--text" style="font-size: 14px;" aria-label="Warning: Attendance Type not configured"></i>
                                    </fe-tooltip>
                                    <fe-info-tip v-else tooltip="Administrators can configure attendance rate preferences in system configuration." icon="fal fa-info-circle" color="#495B8F" size="14px" class="ml-1" />
                                </label>
                                <span
                                    v-if="attendanceTypeNotConfigured"
                                    id="attendance-type-error"
                                    class="sr-only"
                                >No attendance rate types are enabled for your district. Please contact an administrator for help.</span>
                                <v-select
                                    id="attendance-type"
                                    v-model="panelValues.rate_type"
                                    itemText="name" itemValue="value"
                                    :items="attendance_rate_types"
                                    :disabled="attendance_rate_types && attendance_rate_types.length === 1"
                                    :clearable="attendance_rate_types && attendance_rate_types.length > 1"
                                    returnObject
                                    flat solo dense
                                    :aria-invalid="attendanceTypeNotConfigured"
                                    :aria-describedby="attendanceTypeNotConfigured ? 'attendance-type-error' : undefined"
                                />
                            </v-flex>

                            <v-flex xs6 class="min-days-cell">
                                <label for="attendance-min-days-enrolled" class="theme--light v-label row-label">Min Days Students Enrolled</label>
                                <v-text-field
                                    id="attendance-min-days-enrolled"
                                    v-mask="'###'"
                                    v-model="panelValues.min_possible_attendance_days"
                                    flat solo dense
                                />
                            </v-flex>
                        </v-layout>

                        <v-layout row>
                            <fe-remote-combo
                                label="School Year"
                                v-model="panelValues.school_year"
                                rootProperty="years"
                                url="qry.php?action=get&property=school_years"
                            />

                            <v-flex class="pr-2 mt-6">
                                <v-switch v-model="panelValues.restrict" label="Restrict Results" hide-details :ripple="false"/>
                            </v-flex>
                        </v-layout>
                    </v-form>
                </fe-stepper-panel>

                <template #footer>
                </template>
            </fe-stepper-filter>
        </v-dialog>
    </div>
</template>

<script>
import * as Util from '../../Util'
import FilterUtil from '../../FilterPanelUtil'
import AdvancedFilterBtn from './AdvancedFilterBtn'
import escapeCloseDialogMixin from './escapeCloseDialogMixin'
import { MONTHS, WEEKDAYS } from '../../AttendanceConstants'

export default {
    name: 'AttendancePanel',
    mixins: [escapeCloseDialogMixin],
    components: { AdvancedFilterBtn },

    rank: 1,

    inject: ['localStore'],

    data() {
        return {
            formValid: true,
            oldFilter: null,
            displayName: 'Attendance',
            type: 'attendance',
            dialog: false,
            panelValues: {
                restrict: true,
                school_year: null,
                occurrence_month_num: [],
                occurrence_weekday_num: [],
                attendance_value_min: 0,
                attendance_value_max: 100,
                rate_type: null,
                filter_by: 'rate',
                min_possible_attendance_days: '',
            },
            panelOpenState: {},
            persistDialog: false,
            days: WEEKDAYS,
            months: MONTHS,
            attendance_rate_types: [],
            filter_by_options: [
                {name: 'Attendance Rate', id: 'rate'},
                {name: 'Days Missed', id: 'days'}
            ],
        }
    },

    computed: {
            hasAppliedFilter() {
                const filters = this.localStore?.state?.params?.filters
                return !!filters?.some(f => f.type === this.type)
            },
        title() {
            return this.oldFilter ? `Edit ${this.displayName} Filter` : `Create ${this.displayName} Filter`
        },
        enableFinalPanel() {
            return !this.attendanceTypeNotConfigured && !!this.panelValues.rate_type
        },
        attendanceTypeNotConfigured() {
            return !this.attendance_rate_types || this.attendance_rate_types.length === 0
        },
        breadcrumb() {
            let min = this.panelValues.filter_by === 'rate' ? '%' : ''
            let max = this.panelValues.filter_by === 'rate' ? '%' : ' Days Missed'
            return `Attendance: ${this.panelValues.attendance_value_min}${min}-${this.panelValues.attendance_value_max}${max}`
        },
        maxError() {
            let useMaxValue = this.panelValues.filter_by === 'rate'
            return this.panelValues.attendance_value_max === '' ||
            useMaxValue && this.panelValues.attendance_value_max > 100
            ? 'Enter a valid percent value'
            : Number(this.panelValues.attendance_value_min) >
                Number(this.panelValues.attendance_value_max)
                    ? 'Maximum must be greater than minimum'
                    : null
        },
        minError() {
            return this.panelValues.attendance_value_min === '' ||
            this.panelValues.attendance_value_min < 0
                ? 'Enter a valid percent value'
                : Number(this.panelValues.attendance_value_min) >
                Number(this.panelValues.attendance_value_max)
                    ? 'Minimum must be less than maximum'
                    : null
        },
        computedPanelValues() {
            return Object.assign({}, this.panelValues)
        }
    },

    watch: {
        dialog(v) {
            if (!v) this.oldFilter = null, this.clear()
            if (v) {
                this.loadAttendanceRateTypes()
                this.panelOpenState = this.$_.clone(this.panelValues)
            }
        },
        computedPanelValues: {
            handler(newValue) {
                this.persistDialog = FilterUtil.hasFormStateChanged(newValue, this.panelOpenState)
            }
        },
    },

    created() {
        this.loadAttendanceRateTypes()
    },

    methods: {
        loadAttendanceRateTypes() {
            this.attendance_rate_types = []
            Promise.all([
                this.$axios.get(
                    'preferences.php?action=get&property=district&code=ATTENDANCE_EQUIV_RATE'
                ),
                this.$axios.get(
                    'preferences.php?action=get&property=district&code=ATTENDANCE_AFFECTED_RATE'
                )
            ]).then(rs => {
                if (rs[0].data.preferences.some(p => p.user_value === '1')) {
                    this.attendance_rate_types.push({
                        name: 'SIS Reported',
                        value: 'days_equivalent'
                    })
                }

                if (rs[1].data.preferences.some(p => p.user_value === '1')) {
                    this.attendance_rate_types.push({
                        name: 'Full Day',
                        value: 'days_affected'
                    })
                }

                if (this.attendance_rate_types.length && !this.panelValues.rate_type) {
                    this.panelValues.rate_type = this.attendance_rate_types[0]
                }
            })
        },
        clear() {
            this.panelValues = {
                restrict : true,
                school_year : null,
                occurrence_month_num : [],
                occurrence_weekday_num : [],
                attendance_value_min : 0,
                attendance_value_max : 100,
                rate_type : this.attendance_rate_types?.[0],
                min_possible_attendance_days : '',
                filter_by : "rate"
            }
            this.persistDialog = false
        },

        load(oldFilter) {
            this.oldFilter = oldFilter
            this.dialog = true
            let ep = oldFilter.editorParams
            this.panelValues = {
                restrict : ep.restrict,
                school_year : ep.school_year,
                occurrence_month_num : ep.occurrence_month_num,
                occurrence_weekday_num : ep.occurrence_weekday_num,
                attendance_value_min : ep.attendance_value_min,
                attendance_value_max : ep.attendance_value_max,
                rate_type : ep.rate_type,
                filter_by: ep.filter_by,
                min_possible_attendance_days : ep.min_possible_attendance_days,
            }
        },

        emit() {
            if (!this.panelValues.rate_type && this.attendance_rate_types?.length > 0) {
                this.panelValues.rate_type = this.attendance_rate_types[0]
            }
            this.dialog = false
            let uniqueId = Util.UUIDv4()
            let name = this.breadcrumb
            let filter = {
                type: this.type,
                id: uniqueId,
                restrict: this.panelValues.restrict,
                report: true,
                params: this.$_.pickBy({
                    occurrence_month_num: this.panelValues.occurrence_month_num?.map(x => x.value),
                    occurrence_weekday_num: this.panelValues.occurrence_weekday_num?.map(x => x.value),
                    attendance_value_min: this.panelValues.attendance_value_min,
                    attendance_value_max: this.panelValues.attendance_value_max,
                    rate_type: this.panelValues.rate_type?.value,
                    filter_by: this.panelValues.filter_by,
                    min_possible_attendance_days: this.panelValues.min_possible_attendance_days,
                    school_year_id: this.panelValues.school_year?.id,
                    school_id: this.localStore.state.params.search.school_id,
                }, x => !this.$_.isBlank(x)),
            }

            let minProp = this.panelValues.filter_by === 'rate' ? 'Min Rate' : 'Min Days Missed'
            let maxProp = this.panelValues.filter_by === 'rate' ? 'Max Rate' : 'Max Days Missed'
            const formatRate = (v) => (this.panelValues.filter_by === 'rate' && v != null ? `${v}%` : v)

            Object.defineProperties(filter, {
                editor: {value: this},
                editorParams: {
                    value: {
                        restrict: this.panelValues.restrict,
                        occurrence_month_num: this.panelValues.occurrence_month_num ? [...this.panelValues.occurrence_month_num] : null,
                        occurrence_weekday_num: this.panelValues.occurrence_weekday_num ? [...this.panelValues.occurrence_weekday_num] : null,
                        attendance_value_min: this.panelValues.attendance_value_min,
                        attendance_value_max: this.panelValues.attendance_value_max,
                        rate_type: this.panelValues.rate_type ? {...this.panelValues.rate_type} : null,
                        filter_by: this.panelValues.filter_by,
                        min_possible_attendance_days: this.panelValues.min_possible_attendance_days,
                        school_year: this.panelValues.school_year ? {...this.panelValues.school_year} : null,
                    }
                },
                grouper: {
                    value: {
                        id: uniqueId,
                        name: name,
                        type: 'controller',
                        args: {property: `filters.${uniqueId}.value`},
                    }
                },
                chipText: {value: name},
                tooltipData: {
                    value: this.$_.pickBy({
                        'Month(s)': this.panelValues.occurrence_month_num?.map(x => x.name).join(', '),
                        'Weekday(s)': this.panelValues.occurrence_weekday_num?.map(x => x.name).join(', '),
                        'Attendance Type': this.panelValues.rate_type?.name,
                        [minProp]: formatRate(this.panelValues.attendance_value_min),
                        [maxProp]: formatRate(this.panelValues.attendance_value_max),
                        'Min Days Enrolled': this.panelValues.min_possible_attendance_days,
                        'Restrict': this.panelValues.restrict,
                        'School Year': this.panelValues.school_year?.name,
                    }, x => !this.$_.isBlank(x))
                }
            })

            this.$emit('input', filter, this.oldFilter)
            this.oldFilter = filter
        },
    }
}
</script>

<style lang="scss" scoped>
.sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
}

.attendance-type-label.error--text,
.attendance-type-label.error--text i {
    color: #BF0D00 !important;
}

.attendance-type-field--error ::v-deep .v-input__slot {
    border-color: #BF0D00 !important;
}

.attendance-type-cell,
.min-days-cell {
    display: flex;
    flex-direction: column;
}
.attendance-type-cell .row-label,
.min-days-cell .row-label {
    display: flex;
    align-items: center;
    min-height: 24px;
    height: 24px;
}
.attendance-type-cell ::v-deep .v-input__slot,
.min-days-cell ::v-deep .v-input__slot {
    min-height: 42px;
}
</style>
